import { RadioGroup, RadioGroupItemProps } from '@chakra-ui/react';
import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  forwardRef,
} from 'react';

export interface MRadioProps extends Omit<
  RadioGroupItemProps,
  'disabled' | 'invalid'
> {
  isDisabled?: boolean;
  isInvalid?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  id?: string;
  'data-testid'?: string;
  children?: ReactNode;
}

/*
 * Must render inside an `MRadioGroup` (or a raw `RadioGroup.Root`) — v3's
 * `RadioGroup.Item` reads its checked state from the group's context, there
 * is no standalone controlled Radio in v3 at all. Keeps v2's per-item shape
 * (`isDisabled`, `value`, children as the label, an optional `onChange` for
 * call sites that run a side effect when *this* radio becomes selected —
 * confirmed via real native input bubbling, since `ItemHiddenInput` is a
 * real `<input type="radio">`).
 *
 * Same split as `MCheckbox`: `id`/`data-testid`/the event handlers/the
 * forwarded ref land on `ItemHiddenInput` (the real accessible input);
 * everything else (style props, `disabled`/`invalid`, `value`) goes on
 * `Item` itself.
 */
export const MRadio = forwardRef<HTMLInputElement, MRadioProps>(
  (
    {
      isDisabled,
      isInvalid,
      onChange,
      onBlur,
      id,
      'data-testid': dataTestId,
      children,
      ...itemProps
    },
    ref,
  ) => (
    <RadioGroup.Item disabled={isDisabled} invalid={isInvalid} {...itemProps}>
      <RadioGroup.ItemHiddenInput
        ref={ref}
        id={id}
        data-testid={dataTestId}
        onChange={onChange}
        onBlur={onBlur}
      />
      <RadioGroup.ItemIndicator />
      {children && <RadioGroup.ItemText>{children}</RadioGroup.ItemText>}
    </RadioGroup.Item>
  ),
);

MRadio.displayName = 'MRadio';

export default MRadio;
